import { useMemo } from 'react';
import { UserProfile } from '@shared/types';
import { logger } from '@/lib/logger';
import { SELF_PROFILE_FIELDS, isSelfProfileField } from '@shared/profile-edit';

// Fields users can always edit on their own profile.
const SELF_EDITABLE_FIELDS = [
  ...SELF_PROFILE_FIELDS.filter(field => field !== 'password'),
  'newPassword', 'confirmNewPassword',
];

// Fields requiring elevated management privileges.
const MANAGEMENT_FIELDS = [
  'fullName',
  'email',
  'isDoctor',
  'role',
  'organizationId',
  'locationId',
  'locationIds',
  'licenseNumber',
  'caqhNumber',
  'npiNumber',
  'previousWorkExperience',
  'canViewAllScorecards',
  'specificScorecardAccess',
  'headlineAccess',
];

interface PermissionResult {
  canEdit: boolean;
  reason?: string;
  requiredRole?: string;
}

export function useProfilePermissions(currentUser: UserProfile | null) {
  const canEditUser = (targetUser: UserProfile): boolean => {
    if (!currentUser) return false;
    if (currentUser.isSuperAdmin) return true;

    return targetUser.id === currentUser.id;
  };

  const canEditField = (fieldName: string, targetUser: UserProfile): PermissionResult => {
    if (!currentUser) return { canEdit: false, reason: 'Not authenticated' };
    if (currentUser.isSuperAdmin) return { canEdit: true };
    const selfField = isSelfProfileField(fieldName) || ['newPassword', 'confirmNewPassword'].includes(fieldName);
    if (targetUser.id === currentUser.id && selfField) return { canEdit: true };
    return { canEdit: false, reason: 'Only super admins can edit this field' };
  };

  const canEditTab = (
    tabName: 'work' | 'personal' | 'professional' | 'access' | 'locations',
    targetUser: UserProfile,
  ): boolean => {
    if (!currentUser) return false;

    switch (tabName) {
      case 'work':
        return ['fullName', 'email', 'role', 'organizationId', 'isDoctor'].some(
          (field) => canEditField(field, targetUser).canEdit,
        );
      case 'personal':
        return ['personalEmail', 'personalPhone', 'homeAddress', 'birthDate'].some(
          (field) => canEditField(field, targetUser).canEdit,
        );
      case 'professional':
        return ['licenseNumber', 'caqhNumber', 'npiNumber', 'professionalGoals', 'notes'].some(
          (field) => canEditField(field, targetUser).canEdit,
        );
      case 'locations':
        return ['locationId', 'locationIds'].some((field) => canEditField(field, targetUser).canEdit);
      case 'access':
        return ['canViewAllScorecards', 'specificScorecardAccess', 'headlineAccess'].some(
          (field) => canEditField(field, targetUser).canEdit,
        );
      default:
        return false;
    }
  };

  const getEditableFields = (targetUser: UserProfile): string[] => {
    const fields = [...SELF_EDITABLE_FIELDS, ...MANAGEMENT_FIELDS];
    return fields.filter((field) => canEditField(field, targetUser).canEdit);
  };

  const logPermissionCheck = (action: string, targetUser: UserProfile, result: boolean) => {
    logger.debug('Permission check', {
      action,
      currentUser: currentUser?.email,
      currentRole: currentUser?.role,
      targetUser: targetUser.email,
      targetRole: targetUser.role,
      result,
    });
  };

  return useMemo(
    () => ({
      canEditUser,
      canEditField,
      canEditTab,
      getEditableFields,
      isExecutive: Boolean(currentUser?.isSuperAdmin),
      isManager: Boolean(currentUser?.isSuperAdmin),
      logPermissionCheck,
    }),
    [currentUser],
  );
}

export { SELF_EDITABLE_FIELDS, MANAGEMENT_FIELDS };
